/*
 * Quadra, an action puzzle game
 * Copyright (C) 1998-2000  Ludus Design
 *
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU Lesser General Public
 * License as published by the Free Software Foundation; either
 * version 2.1 of the License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the GNU
 * Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public
 * License along with this library; if not, write to the Free Software
 * Foundation, Inc., 59 Temple Place, Suite 330, Boston, MA  02111-1307  USA
 */

export class Stat {
    private value: number;

    constructor() {
        this.value = 0;
    }

    add(n: number): void {
        this.value += n;
    }

    getValue(): number {
        return this.value;
    }

    setValue(i: number): void {
        this.value = i;
    }

    isLessThan(other: Stat): boolean {
        return this.value < other.value;
    }

    isGreaterThan(other: Stat): boolean {
        return this.value > other.value;
    }

    isNotEqualTo(other: Stat): boolean {
        return this.value !== other.value;
    }
}

// Define CS_StatType as a union type
export type CS_StatType =
    'COMPTE0' | 'COMPTE1' | 'COMPTE2' | 'COMPTE3' | 'COMPTE4' | 'COMPTE5' |
    'COMPTE6' | 'COMPTETOT' |
    'CLEAR00' | 'CLEAR01' | 'CLEAR02' | 'CLEAR03' | 'CLEAR04' | 'CLEAR05' |
    'CLEAR06' | 'CLEAR07' | 'CLEAR08' | 'CLEAR09' | 'CLEAR10' | 'CLEAR11' |
    'CLEAR12' | 'CLEAR13' | 'CLEARMORE' |
    'DEATH' | 'FRAG' | 'LINESTOT' | 'SCORE' |
    // 'LAST_BEFORE_120' // Removed as it was likely a marker for the enum, not a real stat
    'CLEAR14' | 'CLEAR15' | 'CLEAR16' | 'CLEAR17' | 'CLEAR18' | 'CLEAR19' | 'CLEAR20' |
    'ROTATED0' | 'ROTATED1' | 'ROTATED2' | 'ROTATED3' | 'ROTATED4' | 'ROTATED5' |
    'ROTATED6' | 'ROTATEDTOT' |
    'PLAYING_TIME' | 'PPM' | 'BPM' | 'ROUND_WINS' |
    'SUICIDES' | 'OVERKILLEE' | 'OVERKILLER' | 'MAXOVERKILLEE' | 'MAXOVERKILLER' |
    'COMBO00' | 'COMBO01' | 'COMBO02' | 'COMBO03' | 'COMBO04' | 'COMBO05' |
    'COMBO06' | 'COMBO07' | 'COMBO08' | 'COMBO09' | 'COMBO10' | 'COMBO11' |
    'COMBO12' | 'COMBO13' | 'COMBO14' | 'COMBO15' | 'COMBO16' | 'COMBO17' |
    'COMBO18' | 'COMBO19' | 'COMBO20' |
    'LINESCUR';

// Array of all CS_StatType keys for iteration
export const CS_STAT_TYPES: CS_StatType[] = [
    'COMPTE0', 'COMPTE1', 'COMPTE2', 'COMPTE3', 'COMPTE4', 'COMPTE5',
    'COMPTE6', 'COMPTETOT',
    'CLEAR00', 'CLEAR01', 'CLEAR02', 'CLEAR03', 'CLEAR04', 'CLEAR05',
    'CLEAR06', 'CLEAR07', 'CLEAR08', 'CLEAR09', 'CLEAR10', 'CLEAR11',
    'CLEAR12', 'CLEAR13', 'CLEARMORE',
    'DEATH', 'FRAG', 'LINESTOT', 'SCORE',
    'CLEAR14', 'CLEAR15', 'CLEAR16', 'CLEAR17', 'CLEAR18', 'CLEAR19', 'CLEAR20',
    'ROTATED0', 'ROTATED1', 'ROTATED2', 'ROTATED3', 'ROTATED4', 'ROTATED5',
    'ROTATED6', 'ROTATEDTOT',
    'PLAYING_TIME', 'PPM', 'BPM', 'ROUND_WINS',
    'SUICIDES', 'OVERKILLEE', 'OVERKILLER', 'MAXOVERKILLEE', 'MAXOVERKILLER',
    'COMBO00', 'COMBO01', 'COMBO02', 'COMBO03', 'COMBO04', 'COMBO05',
    'COMBO06', 'COMBO07', 'COMBO08', 'COMBO09', 'COMBO10', 'COMBO11',
    'COMBO12', 'COMBO13', 'COMBO14', 'COMBO15', 'COMBO16', 'COMBO17',
    'COMBO18', 'COMBO19', 'COMBO20',
    'LINESCUR'
];

// Define GS_StatType as a union type
export type GS_StatType = 'PLAYING_TIME' | 'ROUND_NUMBER';

// Array of all GS_StatType keys for iteration
export const GS_STAT_TYPES: GS_StatType[] = [
    'PLAYING_TIME', 'ROUND_NUMBER'
];

// eslint-disable-next-line @typescript-eslint/no-unused-vars
class CS {
    stats: Record<CS_StatType, Stat>;

    constructor() {
        this.stats = {} as Record<CS_StatType, Stat>; // Initialize as empty object
        for (const key of CS_STAT_TYPES) {
            this.stats[key] = new Stat();
        }
    }

    add(other: CS): void {
        for (const key of CS_STAT_TYPES) {
            this.stats[key].add(other.stats[key].getValue());
        }
    }

    clear(): void {
        for (const key of CS_STAT_TYPES) {
            this.stats[key].setValue(0);
        }
    }

    // Note: The 'better' logic assumes higher is better unless it's 'DEATH'.
    better(other: CS, type: CS_StatType): boolean {
        if (this.stats[type].isGreaterThan(other.stats[type])) {
            return type !== 'DEATH'; // Better if greater, unless it's DEATH
        }
        if (this.stats[type].isLessThan(other.stats[type])) {
             return type === 'DEATH'; // Better if less only if it's DEATH
        }

        // Default comparison logic if values for 'type' are equal
        if (this.stats['FRAG'].isGreaterThan(other.stats['FRAG'])) {
            return true;
        }
        if (this.stats['FRAG'].isLessThan(other.stats['FRAG'])) {
            return false;
        }
        if (this.stats['DEATH'].isLessThan(other.stats['DEATH'])) {
             return true; // Lower deaths are better
        }
        if (this.stats['DEATH'].isGreaterThan(other.stats['DEATH'])) {
             return false;
        }
        if (this.stats['SCORE'].isGreaterThan(other.stats['SCORE'])) {
            return true;
        }
        if (this.stats['SCORE'].isLessThan(other.stats['SCORE'])) {
            return false;
        }
        if (this.stats['LINESTOT'].isGreaterThan(other.stats['LINESTOT'])) {
            return true;
        }
        if (this.stats['LINESTOT'].isLessThan(other.stats['LINESTOT'])) {
            return false;
        }

        return false; // Considered equal or comparison logic exhausted
    }

    static clear_trans(i: number): CS_StatType | undefined {
        // Map number index to corresponding CLEAR stat name
        if (i >= 0 && i <= 13) {
            return `CLEAR${String(i).padStart(2, '0')}` as CS_StatType;
        } else if (i >= 14 && i <= 20) {
            return `CLEAR${String(i)}` as CS_StatType;
        } else if (i > 20) { // Assuming 'CLEARMORE' corresponds to indices > 13 in the original logic? This maps it to > 20 now.
             return 'CLEARMORE';
        }
        return undefined; // Return undefined for invalid indices
    }
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
class GS {
    stats: Record<GS_StatType, Stat>;

    constructor() {
        this.stats = {} as Record<GS_StatType, Stat>; // Initialize as empty object
        for (const key of GS_STAT_TYPES) {
            this.stats[key] = new Stat();
        }
    }

    clear(): void {
        for (const key of GS_STAT_TYPES) {
            this.stats[key].setValue(0);
        }
    }
}

// Example usage (optional, can be removed)
// const clientStats1 = new CS();
// const clientStats2 = new CS();
// clientStats1.stats['SCORE'].setValue(100);
// clientStats2.stats['SCORE'].setValue(150);
// console.log(clientStats1.better(clientStats2, 'SCORE')); // Output: false
// console.log(CS.clear_trans(15)); // Output: 'CLEAR15'

// const gameStats = new GS();
// gameStats.stats['ROUND_NUMBER'].setValue(5);
// console.log(gameStats.stats['ROUND_NUMBER'].getValue()); // Output: 5
// gameStats.clear();
// console.log(gameStats.stats['ROUND_NUMBER'].getValue()); // Output: 0 